import { useCallback, useEffect, useRef } from "react";
import { useConnectionState } from "@/zustand/ConnectionState";
import { SocketEventTypes } from "@/socketIO/types";
import { TYPING_KEEPALIVE_MS } from "@/config/constants";

/**
 * Centralized typing-emit logic. The SINGLE place that emits the `typing` socket
 * event and owns the keep-alive timer, so the keystroke path (useRtfEditor), the
 * accepted-send path (useRtfHandlers), idle/clear, chat-switch, unmount, tab
 * visibility, and socket reconnect/disconnect can never race each other.
 *
 * Contract (sender half of the ephemeral typing protocol):
 *  - First keystroke in a non-empty input -> emit typing=true immediately.
 *  - While typing continues -> re-emit at most once per {@link TYPING_KEEPALIVE_MS}
 *    (8s keep-alive); the receiver expires after 10s.
 *  - Accepted send / input cleared / 8s idle / chat switch / unmount / tab hidden
 *    / window blur / page unload -> emit typing=false and clear the timer.
 *  - Never emit when the input is empty (covers paste-then-delete).
 *  - On socket disconnect -> clear timers (a timer firing into a dead socket is a
 *    no-op but pointless). On reconnect while still typing -> re-emit true so the
 *    indicator (which expired during the outage) comes back without a keystroke.
 *
 * The socket is a persistent singleton that outlives this composer, so all
 * lifecycle teardown is explicit rather than assuming unmount == socket death.
 */
export interface TypingEmitter {
  /**
   * Drive from the keystroke path. Pass the current input text; the hook decides
   * whether to start, keep-alive, or stop based on emptiness + throttle window.
   */
  notifyTyping: (text: string) => void;
  /** Force typing=false now (accepted send, explicit clear). Idempotent. */
  stopTyping: () => void;
}

export function useTypingEmitter(
  chatId: string | null | undefined,
): TypingEmitter {
  // We read the socket reactively so reconnects (which swap the singleton) wire
  // up fresh listeners, but emit through getState() to always hit the live one.
  const socket = useConnectionState((state) => state.socket);

  // Whether we currently believe the local user is typing in `chatId`.
  const isTypingRef = useRef(false);
  // Last time we emitted typing=true (for throttling the keep-alive).
  const lastEmitAtRef = useRef(0);
  // Idle timer: fires typing=false after the keep-alive window with no keystroke.
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Epoch-ms deadline at which the idle timer is due to fire typing=false. Kept
  // separate from the timer handle so a reconnect (which had to clear the timer
  // on disconnect) can tell whether the user has since gone idle while offline.
  const idleDeadlineRef = useRef(0);
  // The chat we last emitted typing into (carried so a chat switch can stop the
  // PREVIOUS chat, not the new one).
  const activeChatRef = useRef<string | null>(null);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  // Arm the idle timer for `delayMs` and record its absolute deadline. The
  // deadline survives a clearIdleTimer() (e.g. on disconnect) so reconnect logic
  // can tell whether idle already elapsed offline.
  const armIdleTimer = useCallback(
    (targetChatId: string, delayMs: number) => {
      clearIdleTimer();
      idleDeadlineRef.current = Date.now() + delayMs;
      idleTimerRef.current = setTimeout(() => {
        // stopTypingFor is referenced lazily via the closure below to avoid a
        // declaration-order cycle; see the wrapper passed in by callers.
        idleFireRef.current(targetChatId);
      }, delayMs);
    },
    [clearIdleTimer],
  );

  // Indirection so armIdleTimer can call stopTypingFor without a TDZ/order cycle.
  const idleFireRef = useRef<(chatId: string) => void>(() => {});

  // Low-level emit. Always targets the live singleton socket.
  const emit = useCallback((targetChatId: string, isTyping: boolean) => {
    const live = useConnectionState.getState().socket;
    if (!live?.connected) {
      return;
    }
    // volatile: typing is ephemeral — if the packet can't go out right now
    // (transport flushing, or a drop racing this connected-check), drop it
    // rather than buffer and deliver a stale burst on reconnect. Matches the
    // SDK's sendTyping() which is volatile for the same reason.
    live.volatile.emit(SocketEventTypes.TYPING, {
      chat_id: targetChatId,
      is_typing: isTyping,
    });
  }, []);

  // Stop typing in a specific chat (idempotent — a second call is a no-op).
  const stopTypingFor = useCallback(
    (targetChatId: string | null) => {
      clearIdleTimer();
      idleDeadlineRef.current = 0;
      if (!isTypingRef.current || !targetChatId) {
        isTypingRef.current = false;
        return;
      }
      isTypingRef.current = false;
      lastEmitAtRef.current = 0;
      emit(targetChatId, false);
    },
    [clearIdleTimer, emit],
  );

  // Keep the idle-timer's fire callback pointed at the latest stopTypingFor.
  idleFireRef.current = stopTypingFor;

  const stopTyping = useCallback(() => {
    stopTypingFor(activeChatRef.current);
  }, [stopTypingFor]);

  const notifyTyping = useCallback(
    (text: string) => {
      if (!chatId) {
        return;
      }
      activeChatRef.current = chatId;

      // Never emit for an empty input. If the user cleared it, stop typing.
      if (text.trim().length === 0) {
        stopTypingFor(chatId);
        return;
      }

      const now = Date.now();

      if (!isTypingRef.current) {
        // First keystroke after idle/empty -> emit immediately.
        isTypingRef.current = true;
        lastEmitAtRef.current = now;
        emit(chatId, true);
      } else if (now - lastEmitAtRef.current >= TYPING_KEEPALIVE_MS) {
        // Still typing past the keep-alive window -> re-emit true.
        lastEmitAtRef.current = now;
        emit(chatId, true);
      }

      // (Re)arm the idle timer: if no keystroke for the keep-alive window,
      // emit typing=false so the indicator clears without waiting the full TTL.
      armIdleTimer(chatId, TYPING_KEEPALIVE_MS);
    },
    [chatId, emit, armIdleTimer, stopTypingFor],
  );

  // Chat switch: when chatId changes, stop typing for the PREVIOUS chat.
  const prevChatIdRef = useRef<string | null | undefined>(chatId);
  useEffect(() => {
    const prev = prevChatIdRef.current;
    prevChatIdRef.current = chatId;
    if (prev && prev !== chatId) {
      stopTypingFor(prev);
    }
  }, [chatId, stopTypingFor]);

  // Component unmount: emit false + clear timer. Idempotent so React 18
  // StrictMode's double-invoke (mount/unmount/mount) does not emit spurious
  // false/true pairs — stopTypingFor no-ops when not currently typing.
  useEffect(() => {
    return () => {
      stopTypingFor(activeChatRef.current);
    };
  }, [stopTypingFor]);

  // Tab hidden / window blur / page unload: best-effort teardown. Unload may not
  // deliver, but the receiver's 10s TTL backstops it.
  useEffect(() => {
    const handleHidden = () => {
      if (typeof document !== "undefined" && document.hidden) {
        stopTypingFor(activeChatRef.current);
      }
    };
    const handleBlurOrUnload = () => {
      stopTypingFor(activeChatRef.current);
    };

    document.addEventListener("visibilitychange", handleHidden);
    window.addEventListener("blur", handleBlurOrUnload);
    window.addEventListener("beforeunload", handleBlurOrUnload);
    window.addEventListener("pagehide", handleBlurOrUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleHidden);
      window.removeEventListener("blur", handleBlurOrUnload);
      window.removeEventListener("beforeunload", handleBlurOrUnload);
      window.removeEventListener("pagehide", handleBlurOrUnload);
    };
  }, [stopTypingFor]);

  // Socket reconnect/disconnect. The singleton swaps on token change/reconnect,
  // so this re-runs when `socket` changes identity.
  useEffect(() => {
    if (!socket) {
      return;
    }

    const handleDisconnect = () => {
      // Clear the timer — a fire into a dead socket is pointless. Keep
      // isTypingRef so reconnect can restore the indicator if still typing.
      clearIdleTimer();
    };

    const handleReconnect = () => {
      const chat = activeChatRef.current;
      if (!isTypingRef.current || !chat) {
        return;
      }

      const now = Date.now();
      // If the idle deadline already lapsed while we were offline, the user has
      // effectively stopped typing — do NOT resurrect a stale indicator. Mark
      // inactive and (best-effort) emit false so peers clear immediately rather
      // than waiting out the TTL.
      if (idleDeadlineRef.current && now >= idleDeadlineRef.current) {
        stopTypingFor(chat);
        return;
      }

      // Still within the idle window: the remote indicator expired during the
      // outage (10s TTL), so re-emit true to bring it back without a keystroke,
      // and re-arm the idle timer for the REMAINING time so it still fires on
      // schedule rather than resetting the full window.
      lastEmitAtRef.current = now;
      emit(chat, true);
      const remaining = idleDeadlineRef.current
        ? Math.max(0, idleDeadlineRef.current - now)
        : TYPING_KEEPALIVE_MS;
      armIdleTimer(chat, remaining);
    };

    socket.on("disconnect", handleDisconnect);
    // socket.io fires "connect" again on every successful (re)connection.
    socket.on("connect", handleReconnect);

    return () => {
      socket.off("disconnect", handleDisconnect);
      socket.off("connect", handleReconnect);
    };
  }, [socket, clearIdleTimer, emit, armIdleTimer, stopTypingFor]);

  return { notifyTyping, stopTyping };
}
